import { useCallback, useMemo, useState } from "react"

import { STEPS } from "../../../enums/ateliers"
import { DetectiveCluxItem, Level } from "./config/interfaces"
import {
  canShowNextClue,
  canShowPreviousClue,
  createInitialState,
  drawClue,
  findItem,
  getSolutionItems,
  getSuspects,
  getViewedClueId,
  goToStep,
  hasRemainingClues,
  isSolutionFound,
  restartGame,
  selectTile as applyTileSelection,
  showNextClue,
  showPreviousClue,
  startGame,
} from "./helpers/game"
import { useSelectionFeedback } from "./useSelectionFeedback"

/**
 * Gère l'état d'une partie du Détective Clux : étapes, pioche, tuiles éteintes et verdict.
 * Toutes les données dynamiques sont dans un seul objet `state`.
 */
export const useDetectiveCluxGame = (items: DetectiveCluxItem[]) => {
  const [state, setState] = useState(createInitialState)
  const { shakingTileId, feedbackMessage, giveFeedback, clearShake } =
    useSelectionFeedback()

  const openEnvelope = useCallback(
    () => setState(current => goToStep(current, STEPS.PARAMETRES)),
    [],
  )
  const chooseLevel = useCallback(
    (level: Level, checkAnswers: boolean) =>
      setState(startGame(items, level, checkAnswers)),
    [items],
  )
  const draw = useCallback(() => setState(current => drawClue(current)), [])
  const showPrevious = useCallback(
    () => setState(current => showPreviousClue(current)),
    [],
  )
  const showNext = useCallback(
    () => setState(current => showNextClue(current)),
    [],
  )
  // L'issue est calculée sur l'état courant pour pouvoir déclencher le son et le tremblement
  const selectTile = useCallback(
    (id: string) => {
      const selection = applyTileSelection(state, id)
      setState(selection.state)
      giveFeedback(id, selection.outcome)
    },
    [state, giveFeedback],
  )
  const showResults = useCallback(
    () => setState(current => goToStep(current, STEPS.RESULTATS)),
    [],
  )
  const restart = useCallback(() => setState(restartGame()), [])

  const viewedClueId = getViewedClueId(state)
  const currentClue = useMemo(
    () => (viewedClueId ? (findItem(items, viewedClueId) ?? null) : null),
    [items, viewedClueId],
  )
  const { disabledTileIds } = state
  const suspects = useMemo(
    () => getSuspects(items, disabledTileIds),
    [items, disabledTileIds],
  )
  const solutionItems = useMemo(
    () => (state.solution ? getSolutionItems(items, state.solution) : null),
    [items, state.solution],
  )

  return {
    state,
    currentClue,
    disabledTileIds,
    suspects,
    solutionItems,
    canDraw: hasRemainingClues(state),
    canShowPrevious: canShowPreviousClue(state),
    canShowNext: canShowNextClue(state),
    isSolved: isSolutionFound(suspects, state.solution),
    shakingTileId,
    feedbackMessage,
    openEnvelope,
    chooseLevel,
    draw,
    showPrevious,
    showNext,
    selectTile,
    clearShake,
    showResults,
    restart,
  }
}
